import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { categories, getCategoryBySlug } from "@/mocks/categories";
import { articlesByCategory, type Article } from "@/mocks/articles";
import NotFound from "@/pages/NotFound";

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const category = getCategoryBySlug(slug || "");
  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = articlesByCategory[category.slug] || [];
  const featuredArticle = categoryArticles[0];
  const gridArticles = categoryArticles.slice(1, 5);

  const webpageLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 51吃瓜网`,
    description: `浏览51吃瓜网${category.name}分类，${category.description}，共${category.articleCount}篇文章。`,
    url: `http://9mzg.cn/category/${category.slug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "51吃瓜网",
      url: "http://9mzg.cn/",
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://9mzg.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: category.name,
      },
    ],
  };

  const subcategories = categories.filter((c) => c.slug !== category.slug);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webpageLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative h-[500px] md:h-[600px] flex items-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=dark%20cinematic%20entertainment%20news%20scene%20with%20warm%20golden%20lighting%2C%20dramatic%20atmosphere%2C%20abstract%20backdrop%20with%20rich%20dark%20greens%20and%20amber%20accents%2C%20editorial%20photography%20style%2C%20mysterious%20and%20powerful%20composition&width=1920&height=1080&seq=category-hero&orientation=landscape"
            alt="51吃瓜网分类"
            title="51吃瓜网分类浏览"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

          <div className="relative z-10 w-full h-full flex flex-col">
            <div className="flex-1 flex items-center">
              <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
                <div className="max-w-2xl">
                  <h1 className="font-serif font-black text-4xl md:text-6xl lg:text-7xl text-white leading-tight mb-4">
                    {category.heroTitle}
                  </h1>
                  <p className="text-white/80 text-lg mb-6">{category.heroSubtitle}</p>
                  <button className="whitespace-nowrap inline-block px-8 py-3.5 rounded-full bg-primary-500/80 backdrop-blur-sm text-white font-medium hover:bg-primary-500 transition-all duration-200 cursor-pointer">
                    {t("category.hero.cta")}
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="pb-16 md:pb-24">
              <div className="max-w-7xl mx-auto px-6 md:px-10">
                <p className="font-serif font-black text-3xl md:text-5xl text-white leading-none">
                  {category.heroSubtitle}
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Breadcrumb
            items={[
              { label: t("common.breadcrumb.home"), href: "/" },
              { label: category.name },
            ]}
          />
        </div>

        {/* Subcategory Grid */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full border border-accent-300 text-accent-700 text-sm font-medium mb-4">
                {t("category.subcategorySection.label")}
              </span>
              <h2 className="font-serif font-bold text-3xl md:text-4xl text-foreground-950 mb-3">
                {t("category.subcategorySection.title1")}
                <br />
                {t("category.subcategorySection.title2")}
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto">
                {t("category.subcategorySection.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {subcategories.map((sub) => (
                <Link
                  key={sub.slug}
                  to={`/category/${sub.slug}`}
                  className="group block bg-white rounded-lg p-6 transition-all duration-300 hover:bg-accent-500 hover:text-white cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-lg bg-background-200 group-hover:bg-white/20 flex items-center justify-center mb-4 transition-colors duration-300">
                    <i className={`${sub.icon} text-xl text-foreground-700 group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <h3 className="font-bold text-lg text-foreground-950 group-hover:text-white mb-2">
                    {sub.name}
                  </h3>
                  <p className="text-foreground-500 text-sm group-hover:text-white/80 mb-4">
                    {sub.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-primary-600 text-sm font-medium group-hover:text-white/90 whitespace-nowrap">
                    {t("category.subcategorySection.viewAll")}
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article Grid */}
        <section className="bg-background-100 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-12">
              <h2 className="font-serif font-bold text-3xl md:text-4xl text-foreground-950 max-w-md leading-tight">
                {category.name}
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-sm">
                {t("category.hotSection.description")}
              </p>
            </div>

            {featuredArticle ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="md:col-span-1">
                  <ArticleCard article={featuredArticle} variant="featured" />
                </div>
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {gridArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} variant="default" />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-20 text-foreground-400">
                <p>暂无文章</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}